import React from 'react';

import { BarChart3, ListOrdered, MapPin } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';

import Loader from '../../components/common/Loader';
import {
  MessageCard,
  OwnerAvatar,
  SharedPageLayout,
} from '../../components/common/SharedPage';
import {
  choiceLabel,
  formatHandicapIndex,
  GAMBLE_PREFERENCES,
  languageLabels,
  PACES_OF_PLAY,
} from '../../constants/GolferProfileConstants';
import { useSharedProfile } from '../../hooks/useProfileShare';
import { SharedHomeClubDTO, SharedProfileDTO } from '../../services/apiClient';
import { courseLogo } from '../../utils/courseClosure';

// A member's profile as anyone with the link sees it: /shared/profile/:shareId. Public, no
// sign-in. The code in the URL is a share code the member made with "Share profile" on their own
// profile (and can revoke), never their member id.
//
// It reads like the top of the member's own profile, Instagram style: picture, name and the three
// counters, the bio under them, then the primary home club, golf index and where they live, then the golfer
// profile answers. Every way the link can be wrong ends on a plain message, as on a shared list.

const COUNTERS: {
  label: [string, string];
  value: (profile: SharedProfileDTO) => number | undefined;
}[] = [
  { label: ['course', 'courses'], value: (p) => p.coursesPlayed },
  { label: ['country', 'countries'], value: (p) => p.countriesPlayed },
  { label: ['bucket list', 'bucket list'], value: (p) => p.bucketList },
];

const yesNo = (value: boolean | undefined): string | null =>
  value == null ? null : value ? 'Yes' : 'No';

const HomeClub: React.FC<{ club: SharedHomeClubDTO }> = ({ club }) => {
  const name = (
    <span className="text-sm font-medium text-white">
      {club.courseName || 'Unknown course'}
    </span>
  );

  return (
    <li className="flex items-center gap-3">
      {/* White plate and the GolfPlayed mark for a club with no logo, as on a shared list. */}
      <span className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded bg-white p-0.5 ring-1 ring-black/20">
        <img
          src={courseLogo(club).src}
          alt=""
          className="h-full w-full object-contain"
          loading="lazy"
          draggable={false}
        />
      </span>
      {/* Only courses the public course page can show are links. */}
      {club.hasProfile && club.courseId != null ? (
        <Link
          to={`/courses/${club.courseId}`}
          className="rounded underline-offset-2 hover:text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          {name}
        </Link>
      ) : (
        name
      )}
    </li>
  );
};

const ProfileBody: React.FC<{ profile: SharedProfileDTO }> = ({ profile }) => {
  const index = formatHandicapIndex(profile.handicapIndex);
  const location = [profile.stateName, profile.countryName]
    .filter((part) => part != null && part.trim().length > 0)
    .join(', ');
  const club = profile.homeClub;

  // Only the answers the member gave. A card of "Not set" rows tells a stranger nothing.
  const details = [
    { label: 'Golf index', value: index },
    {
      label: 'Like to gamble',
      value: choiceLabel(GAMBLE_PREFERENCES, profile.gamblePreference),
    },
    {
      label: 'Pace of play',
      value: choiceLabel(PACES_OF_PLAY, profile.paceOfPlay),
    },
    {
      label: 'Languages',
      value: languageLabels(profile.languages).join(', ') || null,
    },
    { label: 'Play as a couple', value: yesNo(profile.playsAsCouple) },
  ].filter((row) => row.value != null);

  return (
    <>
      <div className="flex items-center gap-4">
        <OwnerAvatar
          name={profile.displayName ?? ''}
          pictureUrl={profile.pictureUrl}
          className="h-20 w-20 text-xl"
        />
        <div className="min-w-0 flex-1">
          <h1 className="break-words font-display text-2xl font-bold leading-tight">
            {profile.displayName}
          </h1>
          <ul className="mt-2 grid max-w-sm grid-cols-3 gap-2">
            {COUNTERS.map((counter) => {
              const value = counter.value(profile) ?? 0;
              return (
                <li key={counter.label[1]}>
                  <span className="block font-display text-lg font-semibold leading-tight text-primary">
                    {value.toLocaleString()}
                  </span>
                  <span className="block whitespace-nowrap text-xs text-white/60">
                    {value === 1 ? counter.label[0] : counter.label[1]}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      </div>

      {profile.about && (
        <p className="mt-4 whitespace-pre-line break-words text-sm text-white/80">
          {profile.about}
        </p>
      )}

      {(club != null || index != null || location.length > 0) && (
        <ul className="mt-4 space-y-2">
          {club != null && <HomeClub club={club} />}
          {index != null && (
            <li className="flex items-center gap-3">
              <span className="flex h-8 w-8 items-center justify-center">
                <BarChart3 className="h-5 w-5 text-white/60" />
              </span>
              <span className="text-sm">Golf index {index}</span>
            </li>
          )}
          {location.length > 0 && (
            <li className="flex items-center gap-3">
              <span className="flex h-8 w-8 items-center justify-center">
                <MapPin className="h-5 w-5 text-white/60" />
              </span>
              <span className="text-sm">Lives in {location}</span>
            </li>
          )}
        </ul>
      )}

      {/* Only when the member shares their list too. Sharing the profile never shares the list. */}
      {profile.coursesPlayedShareId != null && (
        <Link
          to={`/shared/courses-played/${profile.coursesPlayedShareId.toLowerCase()}`}
          className="mt-5 flex items-center justify-center gap-2 rounded-md border border-primary/60 px-4 py-2.5 text-sm font-semibold text-primary transition hover:bg-primary hover:text-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          <ListOrdered className="h-4 w-4" />
          See the courses played
        </Link>
      )}

      {details.length > 0 && (
        <section className="mt-6 rounded-xl border border-primary/30 bg-charcoal-light">
          <h2 className="border-b border-white/10 px-4 py-3 font-display text-base font-semibold">
            Golfer profile
          </h2>
          <dl className="divide-y divide-white/10">
            {details.map((row) => (
              <div
                key={row.label}
                className="flex items-center justify-between gap-4 px-4 py-3 text-sm"
              >
                <dt className="text-white/60">{row.label}</dt>
                <dd className="text-right font-medium">{row.value}</dd>
              </div>
            ))}
          </dl>
        </section>
      )}
    </>
  );
};

const SharedProfile: React.FC = () => {
  const { shareId } = useParams<{ shareId: string }>();
  const state = useSharedProfile(shareId);

  return (
    <SharedPageLayout showCallToAction={state.status === 'ready'}>
      {state.status === 'loading' && (
        <div className="flex justify-center py-16">
          <Loader />
        </div>
      )}

      {state.status === 'invalid' && (
        <MessageCard
          title="This link isn't valid."
          body="Check that the whole link was copied, then try again."
        />
      )}

      {state.status === 'notShared' && (
        <MessageCard
          title="This profile isn't shared."
          body="The link may be old, or its owner may have stopped sharing it."
        />
      )}

      {state.status === 'error' && (
        <MessageCard
          title="Could not load this profile."
          body="Check your connection and try again."
        />
      )}

      {state.status === 'ready' && <ProfileBody profile={state.profile} />}
    </SharedPageLayout>
  );
};

export default SharedProfile;
